import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, Store, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/tenants", label: "Businesses", icon: Store },
  { to: "/users", label: "Users", icon: Users },
];

/**
 * The navigation links — the desktop sidebar and the phone/tablet drawer
 * share them. [layoutId] keeps the two active pills from animating into
 * each other; [onNavigate] lets the drawer close itself on a tap.
 */
export function NavList({ layoutId, onNavigate }: { layoutId: string; onNavigate?: () => void }) {
  const location = useLocation();
  return (
    <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
      {NAV_ITEMS.map((item) => {
        const active = item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className="relative flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-3 text-sm font-medium transition-colors lg:py-2.5"
          >
            {active && (
              <motion.div
                layoutId={layoutId}
                className="absolute inset-0 rounded-[var(--radius-md)] bg-gradient-sunset shadow-glow"
                transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
              />
            )}
            <item.icon
              className={cn("relative z-10 h-4 w-4", active ? "text-primary-foreground" : "text-muted-foreground")}
            />
            <span className={cn("relative z-10", active ? "text-primary-foreground" : "text-foreground")}>
              {item.label}
            </span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export function SidebarFooter() {
  return (
    <div className="border-t border-border px-5 py-3 text-[11px] text-muted-foreground">
      OSF Admin v0.1.0 · {import.meta.env.MODE}
    </div>
  );
}

/** Desktop (lg and up). Phones and tablets get the same links in a drawer — see Topbar. */
export function Sidebar() {
  return (
    <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card lg:flex">
      <div className="flex h-20 items-center border-b border-border px-5">
        <Logo variant="wordmark" />
      </div>
      <NavList layoutId="sidebar-active" />
      <SidebarFooter />
    </aside>
  );
}
